// Page Me — the store's pure logic. No Vue, no storage, no network, so every
// rule here is testable in plain Node (test/client/model.test.js).
//
// src/store.js wraps these in refs and persistence; nothing else should need
// to import this file.

import { OPERATOR_NUMBER, displayName } from './format.js';
import { codeFor } from './codebook.js';

// The display rule lives in format.js (screens use it too); re-exported so the
// store keeps one import.
export { displayName };

export const PAGE_CAP = 2000;
export const INBOX_SIZE = 50;

/** The OPERATOR as a local Friend record. */
export function operatorFriend(at = Date.now()) {
  return {
    number: OPERATOR_NUMBER, name: '', handle: 'OPERATOR', status: 'accepted',
    addedAt: at, lastPageAt: 0, system: true,
  };
}

/**
 * The server's FULL friend list → the local one. Local-only fields (name,
 * addedAt, lastPageAt, hidden) survive; anything the server no longer lists is
 * gone (except local `member` records — named group-mates). The OPERATOR is
 * always system: true.
 */
export function mergeFriends(local, server, at = Date.now()) {
  const byNumber = new Map((local ?? []).map((f) => [f.number, f]));
  const out = [];
  for (const s of server ?? []) {
    if (!s?.number) continue;
    const prev = byNumber.get(s.number);
    const f = {
      number: s.number,
      name: prev?.name ?? '',
      // The server's list is the truth for the name they chose: a changed —
      // or cleared — handle reaches every friend on their next inbox.
      handle: 'handle' in s ? (s.handle || null) : (prev?.handle ?? null),
      status: s.status,
      addedAt: prev?.addedAt ?? s.since ?? at,
      lastPageAt: prev?.lastPageAt ?? 0,
      system: !!s.system || s.number === OPERATOR_NUMBER,
    };
    if (s.firstPage) f.firstPage = s.firstPage;
    if (prev?.hidden) f.hidden = true;
    out.push(f);
  }
  // A group-mate you named but aren't friends with: a local-only record
  // (status `member`) the server never lists. Keep it, so the name sticks.
  const listed = new Set(out.map((f) => f.number));
  for (const f of local ?? []) if (f.status === 'member' && !listed.has(f.number)) out.push(f);
  return out;
}

/** One friend from a single-friend server response, merged into the list. */
export function upsertFriend(local, serverFriend, extra = {}, at = Date.now()) {
  const list = [...(local ?? [])];
  const i = list.findIndex((f) => f.number === serverFriend.number);
  const prev = i >= 0 ? list[i] : null;
  const f = {
    number: serverFriend.number,
    name: extra.name ?? prev?.name ?? '',
    handle: serverFriend.handle ?? prev?.handle ?? null,
    status: serverFriend.status,
    addedAt: prev?.addedAt ?? at,
    lastPageAt: prev?.lastPageAt ?? 0,
    system: !!serverFriend.system || serverFriend.number === OPERATOR_NUMBER,
  };
  if (serverFriend.firstPage) f.firstPage = serverFriend.firstPage;
  if (prev?.hidden) f.hidden = true;
  if (i >= 0) list[i] = f; else list.push(f);
  return list;
}

/** A ServerPage → a local received Page. */
export function pageFromServer(sp, at = Date.now()) {
  const page = {
    id: String(sp.id),
    clientId: sp.clientId ?? null,
    dir: 'in',
    peer: sp.from,
    body: String(sp.body ?? ''),
    sentAt: sp.sentAt ?? at,
    receivedAt: at,
    status: 'delivered',
    read: false,
    locked: false,
  };
  if (sp.groupId) {
    page.groupId = String(sp.groupId);
    if (sp.groupName) page.groupName = String(sp.groupName);
    // A group-mate may not be a friend and may leave: keep the name they had.
    if (sp.fromHandle) page.fromHandle = String(sp.fromHandle);
  }
  return page;
}

/**
 * Merge server pages into local ones, deduped by id.
 * @returns {{ pages: object[], added: object[] }} pages newest first; added oldest first.
 */
export function mergeIncoming(pages, serverPages, at = Date.now()) {
  const have = new Set((pages ?? []).map((p) => p.id));
  const added = [];
  for (const sp of serverPages ?? []) {
    if (!sp?.id || have.has(String(sp.id))) continue;
    have.add(String(sp.id));
    added.push(pageFromServer(sp, at));
  }
  added.sort((a, b) => a.sentAt - b.sentAt);
  return { pages: sortPages([...added, ...(pages ?? [])]), added };
}

/**
 * Apply delivery receipts to outgoing pages by clientId.
 * @returns {{ pages: object[], changed: object[] }}
 */
export function applyReceipts(pages, receipts) {
  const byClient = new Map();
  for (const r of receipts ?? []) {
    if (!r?.clientId) continue;
    if (!byClient.has(r.clientId)) byClient.set(r.clientId, []);
    byClient.get(r.clientId).push(r);
  }
  if (!byClient.size) return { pages, changed: [] };
  const changed = [];
  const out = (pages ?? []).map((p) => {
    if (p.dir !== 'out') return p;
    const rs = byClient.get(p.clientId);
    if (!rs) return p;
    if (p.groupId) {
      const next = applyGroupReceipts(p, rs);
      if (next !== p) changed.push(next);
      return next;
    }
    const r = rs[rs.length - 1];
    if (p.status === r.status || p.status === 'failed') return p;
    if (p.status === 'delivered' && r.status === 'expired') return p;
    const next = { ...p, status: r.status };
    changed.push(next);
    return next;
  });
  return { pages: out, changed };
}

/**
 * A group page has one receipt per recipient (`by`). It is `delivered` once
 * every recipient has it, `expired` if none ever did; in between it stays sent
 * and the mark counts: D 3/5.
 */
function applyGroupReceipts(p, rs) {
  const delivered = new Set(p.deliveredBy ?? []);
  const expired = new Set(p.expiredBy ?? []);
  for (const r of rs) {
    if (!r.by) continue;
    if (r.status === 'delivered') { delivered.add(r.by); expired.delete(r.by); }
    else if (r.status === 'expired' && !delivered.has(r.by)) expired.add(r.by);
  }
  const same = delivered.size === (p.deliveredBy ?? []).length && expired.size === (p.expiredBy ?? []).length;
  if (same) return p;
  const next = { ...p, deliveredBy: [...delivered], expiredBy: [...expired] };
  const n = Number(p.recipients) || 0;
  if (p.status !== 'failed' && n) {
    if (delivered.size >= n) next.status = 'delivered';
    else if (!delivered.size && expired.size >= n) next.status = 'expired';
    else if (p.status === 'queued') next.status = 'network';
  }
  return next;
}

/** Where a page belongs: its group (`g:<id>`), or the friend it was to/from. */
export const convKey = (p) => (p?.groupId ? `g:${p.groupId}` : p?.peer);

/** Newest first, by sentAt then receivedAt. Stable for equal stamps. */
export function sortPages(pages) {
  return [...pages].sort((a, b) => (b.sentAt - a.sentAt) || ((b.receivedAt ?? 0) - (a.receivedAt ?? 0)));
}

/**
 * Keep at most `cap` pages: the oldest UNLOCKED ones go first. Queued pages are
 * never dropped (they have not been sent yet).
 * @returns {{ pages: object[], dropped: object[] }}
 */
export function capPages(pages, cap = PAGE_CAP) {
  if (pages.length <= cap) return { pages, dropped: [] };
  let excess = pages.length - cap;
  const sorted = sortPages(pages);
  const drop = new Set();
  for (let i = sorted.length - 1; i >= 0 && excess > 0; i -= 1) {
    const p = sorted[i];
    if (p.locked || p.status === 'queued') continue;
    drop.add(p.id);
    excess -= 1;
  }
  return {
    pages: sorted.filter((p) => !drop.has(p.id)),
    dropped: sorted.filter((p) => drop.has(p.id)),
  };
}

/**
 * Conversation rows, newest first: [{ key, friend, group, last, unread }].
 * One row per friend (1:1 pages) and one per group you are in (its pages from
 * everyone, even before the first page). `friend` is null on a group row and
 * `group` null on a friend row. Pages from a pager you blocked are left out.
 */
export function buildConversations(pages, friends, groups = []) {
  const blocked = new Set((friends ?? []).filter((f) => f.status === 'blocked').map((f) => f.number));
  const byKey = new Map();
  for (const p of pages) {                       // pages are newest first
    if (p.groupId && p.dir === 'in' && blocked.has(p.peer)) continue;
    const key = convKey(p);
    let c = byKey.get(key);
    if (!c) { c = { last: p, unread: 0 }; byKey.set(key, c); }
    if (p.dir === 'in' && !p.read) c.unread += 1;
  }
  const fmap = new Map((friends ?? []).map((f) => [f.number, f]));
  const gmap = new Map((groups ?? []).map((g) => [g.id, g]));
  const rows = [];
  for (const [key, c] of byKey) {
    if (key.startsWith('g:')) {
      const id = key.slice(2);
      const group = gmap.get(id);
      if (!group) continue;                      // left or gone: its pages stay in the inbox
      rows.push({ key, friend: null, group, last: c.last, unread: c.unread });
      continue;
    }
    const friend = fmap.get(key) ?? {
      number: key, name: '', handle: null, status: 'unknown', addedAt: 0, lastPageAt: c.last.sentAt, system: false,
    };
    if (friend.status === 'blocked') continue;
    rows.push({ key, friend, group: null, last: c.last, unread: c.unread });
  }
  for (const group of groups ?? []) {
    if (!byKey.has(`g:${group.id}`)) rows.push({ key: `g:${group.id}`, friend: null, group, last: null, unread: 0 });
  }
  const at = (r) => r.last?.sentAt ?? r.group?.updatedAt ?? r.group?.createdAt ?? 0;
  return rows.sort((a, b) => at(b) - at(a));
}

/**
 * A group's thread, oldest first: its pages, with its membership changes
 * between them as system lines `{ system: true, id, sentAt, event }`.
 */
export function groupTimeline(pages, group) {
  if (!group) return [];
  const own = pages.filter((p) => p.groupId === group.id);
  const lines = (group.events ?? []).map((e, i) => ({ system: true, id: `ev-${group.id}-${i}-${e.at}`, sentAt: e.at, event: e }));
  return [...own, ...lines].sort((a, b) => (a.sentAt - b.sentAt) || (a.system === b.system ? 0 : a.system ? -1 : 1));
}

/**
 * A membership change in the LCD voice: "JESS JOINED", "DAZ LEFT",
 * "SAM RENAMED IT CREW". `nameOf(number)` applies the display rule.
 */
export function eventLine(e, nameOf) {
  const who = (n, handle) => String(nameOf(n, handle) ?? '').toUpperCase();
  switch (e?.kind) {
    case 'created': return `${who(e.by, e.byHandle)} STARTED ${e.name ?? 'THE GROUP'}`;
    case 'joined': return `${who(e.who, e.whoHandle)} JOINED`;
    case 'left': return `${who(e.who, e.whoHandle)} LEFT`;
    case 'removed': return `${who(e.who, e.whoHandle)} WAS REMOVED`;
    case 'renamed': return `${who(e.by, e.byHandle)} RENAMED IT ${e.name}`;
    default: return '';
  }
}

/**
 * The server's FULL group list → the local one. Server fields win; nothing
 * local is kept except a group's `hidden` flag.
 */
export function mergeGroups(local, server) {
  const prev = new Map((local ?? []).map((g) => [g.id, g]));
  return (server ?? []).filter((g) => g?.id).map((g) => ({
    id: String(g.id),
    name: String(g.name ?? ''),
    owner: g.owner ?? null,
    members: (g.members ?? []).map((m) => ({ number: String(m.number), handle: m.handle ?? null })),
    events: Array.isArray(g.events) ? g.events : [],
    createdAt: g.createdAt ?? prev.get(g.id)?.createdAt ?? 0,
    updatedAt: g.updatedAt ?? 0,
    lastPageAt: g.lastPageAt ?? null,
  }));
}

/** Accepted friends, OPERATOR first, then A–Z by display name. */
export function sortAccepted(friends) {
  return friends
    .filter((f) => f.status === 'accepted' && !f.hidden)
    .sort((a, b) => {
      if (a.system !== b.system) return a.system ? -1 : 1;
      return displayName(a).localeCompare(displayName(b), undefined, { sensitivity: 'base' });
    });
}

/** Odometer: sent, received, the most-used codebook code (or null). */
export function computeStats(pages) {
  let sent = 0;
  let received = 0;
  const codes = {};
  for (const p of pages) {
    if (p.dir === 'out') { if (p.status !== 'failed') sent += 1; } else received += 1;
    const c = codeFor(p.body);
    if (c) codes[c.code] = (codes[c.code] ?? 0) + 1;
  }
  let topCode = null;
  for (const [code, n] of Object.entries(codes)) {
    if (!topCode || n > codes[topCode]) topCode = code;
  }
  return { sent, received, topCode, codes };
}

/** Plain-text export: "2026-09-24 15:42  FROM SAM  07734 👋  [D]" oldest first. */
export function exportLines(pages, nameOf, stampOf, markOf) {
  return [...pages]
    .sort((a, b) => a.sentAt - b.sentAt)
    .map((p) => {
      const who = `${p.dir === 'in' ? 'FROM' : 'TO'} ${String(nameOf(p.peer, p)).toUpperCase()}`;
      return `${stampOf(p.sentAt)}  ${who}  ${p.body}  [${markOf(p.status)}]`;
    })
    .join('\n');
}

/** Move an array item (presets reorder). */
export function moveItem(list, from, to) {
  const out = [...list];
  if (from < 0 || from >= out.length) return out;
  const [item] = out.splice(from, 1);
  out.splice(Math.max(0, Math.min(to, out.length)), 0, item);
  return out;
}

/** A clientId the server accepts (/^[A-Za-z0-9_-]{8,64}$/). */
export function newClientId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Retry delay after the n-th consecutive failure (0-based), or null = wait for an event. */
export const BACKOFF = [5_000, 15_000, 60_000];
export function backoffDelay(n) {
  return n < BACKOFF.length ? BACKOFF[n] : null;
}
